import { useState } from "react";
import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, X, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { convertPDFToText } from "@/services/pdfConversionService";
import { DocumentProcessor } from "@/utils/documentProcessor";
import { supabase } from "@/integrations/supabase/client";

interface LinkedInWizardStep6Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

export function LinkedInWizardStep6({ data, updateData }: LinkedInWizardStep6Props) {
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingFiles, setProcessingFiles] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const uploadPdfToStorage = async (file: File): Promise<string> => {
    const fileName = `${Date.now()}-${file.name}`;
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('uploaded-documents')
      .upload(fileName, file);

    if (uploadError) {
      throw new Error('Failed to upload PDF to storage');
    }

    const { data: { publicUrl } } = supabase.storage
      .from('uploaded-documents')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  const processFile = async (file: File): Promise<string> => {
    const fileKey = `${file.name}-${file.size}`;
    setProcessingFiles(prev => new Set([...prev, fileKey]));

    try {
      let extractedText = '';

      if (file.type === 'application/pdf') {
        const fileUrl = await uploadPdfToStorage(file);
        const result = await convertPDFToText(fileUrl);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to extract text from PDF');
        }
        extractedText = result.text || '';
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword' ||
        file.type === 'text/plain'
      ) {
        const result = await DocumentProcessor.processDocument(file);
        extractedText = result.text;
      } else {
        throw new Error('Unsupported file type');
      }

      return extractedText;
    } finally {
      setProcessingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileKey);
        return newSet;
      });
    }
  };

  const handleFiles = async (files: File[]) => {
    const validFiles = files.filter(file => {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a supported file type.`,
          variant: "destructive"
        });
        return false;
      }
      
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} is larger than 10MB.`,
          variant: "destructive"
        });
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) return;

    setIsProcessing(true);
    const currentFiles = data.uploadedFiles || [];
    const newFiles = [...currentFiles];

    for (const file of validFiles) {
      // Add file with pending status immediately
      const fileEntry = {
        file,
        extractedText: '',
        processingStatus: 'processing' as const,
        processingProgress: 0
      };
      newFiles.push(fileEntry);
    }

    updateData({ uploadedFiles: newFiles });

    // Process each file
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileIndex = currentFiles.length + i;

      try {
        const extractedText = await processFile(file);
        
        // Update the specific file's status
        updateData({
          uploadedFiles: newFiles.map((fileEntry, index) => 
            index === fileIndex 
              ? { ...fileEntry, extractedText, processingStatus: 'completed' as const }
              : fileEntry
          )
        });

        toast({
          title: "File processed successfully",
          description: `Extracted text from ${file.name}`,
        });
      } catch (error) {
        // Update the specific file's status to error
        updateData({
          uploadedFiles: newFiles.map((fileEntry, index) => 
            index === fileIndex 
              ? { ...fileEntry, processingStatus: 'error' as const }
              : fileEntry
          )
        });

        toast({
          title: "Processing failed",
          description: `Failed to extract text from ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
          variant: "destructive"
        });
      }
    }

    setIsProcessing(false);
  };

  const removeFile = (index: number) => {
    const currentFiles = data.uploadedFiles || [];
    const newFiles = currentFiles.filter((_, i) => i !== index);
    updateData({ uploadedFiles: newFiles });
  };

  const handleTextSubmit = () => {
    const text = data.pastedText?.trim();
    if (!text) {
      toast({
        title: "No text entered",
        description: "Please enter some text before submitting.",
        variant: "destructive"
      });
      return;
    }

    // Create a text entry that looks like a file entry
    const timestamp = Date.now();
    const textEntry = {
      file: {
        name: `Text Entry - ${new Date().toLocaleDateString()}`,
        size: text.length,
        type: 'text/plain'
      } as File,
      extractedText: text,
      processingStatus: 'completed' as const,
      processingProgress: 100
    };

    // Add to uploaded files list
    const currentFiles = data.uploadedFiles || [];
    updateData({ 
      uploadedFiles: [...currentFiles, textEntry],
      pastedText: '' // Clear the text input after saving
    });

    toast({
      title: "Text saved successfully",
      description: "Your text has been added to the content list.",
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const hasContent = () => {
    const hasFiles = data.uploadedFiles && data.uploadedFiles.length > 0;
    const hasPastedText = data.pastedText && data.pastedText.trim().length > 0;
    const hasManualText = data.manuallyEnteredText && data.manuallyEnteredText.trim().length > 0;
    return hasFiles || hasPastedText || hasManualText;
  };

  return (
    <div className="space-y-6">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-2xl font-bold text-white">6</span>
          </div>
        </div>
        <div className="ml-12">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">Add Your Information</h2>
          <p className="text-linkedin/70 mb-6">
            Upload documents, paste text, or type manually to provide information for your LinkedIn profile. All methods support text extraction and analysis.
          </p>
        </div>
      </div>

      {/* Profile Package intake: LinkedIn URL + headshot for the branded
          LinkedIn Profile Package PDF cover. Stored with the profile. */}
      <Card className="p-4 border-linkedin/20 space-y-4">
        <div>
          <Label htmlFor="li-url" className="font-semibold">Your LinkedIn profile URL</Label>
          <Input
            id="li-url"
            placeholder="www.linkedin.com/in/your-name"
            value={data.linkedinUrl ?? ''}
            onChange={(e) => updateData({ linkedinUrl: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="font-semibold">Headshot photo (used on your Profile Package cover)</Label>
          <div className="flex items-center gap-3 mt-1">
            <Input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  const user = (await supabase.auth.getUser()).data.user;
                  if (!user) throw new Error('Not signed in');
                  const path = `${user.id}/headshot-${Date.now()}-${f.name.replace(/[^A-Za-z0-9._-]/g, '_')}`;
                  const { error } = await supabase.storage.from('uploaded-documents').upload(path, f);
                  if (error) throw error;
                  const { data: urlData } = supabase.storage.from('uploaded-documents').getPublicUrl(path);
                  updateData({ photoUrl: urlData.publicUrl });
                  toast({ title: 'Headshot uploaded' });
                } catch (err: any) {
                  toast({ title: 'Headshot upload failed', description: err?.message ?? String(err), variant: 'destructive' });
                }
              }}
            />
            {data.photoUrl && <img src={data.photoUrl} alt="headshot" className="w-12 h-12 object-cover border" />}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-2 border-linkedin/20">
          <TabsTrigger 
            value="upload"
            className="data-[state=active]:bg-linkedin data-[state=active]:text-white border-linkedin/20 data-[state=active]:border-linkedin"
          >
            Upload Files
          </TabsTrigger>
          <TabsTrigger 
            value="text"
            className="data-[state=active]:bg-linkedin data-[state=active]:text-white border-linkedin/20 data-[state=active]:border-linkedin"
          >
            Add Text
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          <Card 
            className={`p-8 border-2 border-dashed transition-colors ${
              dragActive 
                ? 'border-primary bg-primary/5' 
                : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="text-center space-y-4">
              <Upload className="w-12 h-12 text-muted-foreground mx-auto" />
              <div>
                <h3 className="font-semibold text-lg">Drop files here or click to upload</h3>
                <p className="text-muted-foreground">
                  Accepted file types: PDF, DOC, DOCX, TXT (Max 10MB each)
                </p>
              </div>
              <Button 
                variant="outline" 
                className="cursor-pointer" 
                disabled={isProcessing}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                {isProcessing ? 'Processing...' : 'Choose Files'}
              </Button>
              <Input
                id="file-upload"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileInput}
                className="hidden"
                disabled={isProcessing}
              />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="text" className="space-y-4">
          <Card className="p-6">
            <div className="space-y-4">
              <Label htmlFor="text-input">Paste or Type Your Text</Label>
              <Textarea
                id="text-input"
                placeholder="Paste your resume, cover letter, job description, or type your professional information, experience, skills, achievements, or any other details you'd like to include..."
                value={data.pastedText || ''}
                onChange={(e) => updateData({ pastedText: e.target.value })}
                className="min-h-[200px]"
              />
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {data.pastedText ? `${data.pastedText.length} characters` : '0 characters'}
                </p>
                <Button 
                  onClick={handleTextSubmit}
                  disabled={!data.pastedText?.trim()}
                >
                  Save Text
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Content Added section - always visible */}
          {data.uploadedFiles && data.uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-linkedin">Content Added ({data.uploadedFiles.length})</h3>
          <div className="space-y-2">
            {data.uploadedFiles.map((fileEntry, index) => (
              <Card key={index} className="p-3 bg-linkedin-light dark:bg-linkedin-dark/20 border-linkedin/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(fileEntry.processingStatus)}
                    <div className="flex-1">
                      <p className="font-medium text-sm text-linkedin">{fileEntry.file.name}</p>
                      <p className="text-xs text-linkedin/70">
                        {fileEntry.file.type === 'text/plain' ? 
                          `${fileEntry.extractedText?.length || 0} characters` :
                          formatFileSize(fileEntry.file.size)
                        }
                        {fileEntry.processingStatus === 'completed' && fileEntry.extractedText && (
                          <span className="ml-2 text-linkedin/80">
                            • {fileEntry.extractedText.length} characters extracted
                          </span>
                        )}
                        {fileEntry.processingStatus === 'processing' && (
                          <span className="ml-2 text-blue-600">• Processing...</span>
                        )}
                        {fileEntry.processingStatus === 'error' && (
                          <span className="ml-2 text-red-600">• Processing failed</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {fileEntry.processingStatus === 'processing' && (
                  <Progress value={33} className="mt-2 h-1" />
                )}
              </Card>
            ))}
          </div>
        </div>
      )}


      <div className="space-y-4">
        <h3 className="font-semibold">Recommended information to provide:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-4">
            <h4 className="font-medium mb-2">Professional Documents</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Current resume (PDF/DOCX)</li>
              <li>• Cover letters</li>
              <li>• Professional bio</li>
              <li>• Portfolio samples</li>
            </ul>
          </Card>
          <Card className="p-4">
            <h4 className="font-medium mb-2">Career Information</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Job descriptions you're targeting</li>
              <li>• Achievement summaries</li>
              <li>• Client testimonials</li>
              <li>• Project descriptions</li>
            </ul>
          </Card>
        </div>
      </div>

      {!hasContent() && (
        <Card className="p-4 bg-muted/50">
          <p className="text-muted-foreground text-sm">
            <strong>No information added yet:</strong> You can proceed to the next steps and provide information manually through the following questions, or add content using any of the methods above.
          </p>
        </Card>
      )}

      {hasContent() && (
        <Card className="p-4 bg-linkedin-light border-linkedin/20">
          <p className="text-linkedin text-sm">
            <strong>Great!</strong> We have your information and will use it to create a more personalized LinkedIn profile. You can add more content using any of the methods above, or proceed to the next step.
          </p>
        </Card>
      )}
    </div>
  );
}